/**
 * Real-time multiplayer asteroids game with TypeScript/Bun server and HTML5 Canvas client
 * Bug reporting domain - Client-side bug report submission and state collection
 * bug.client.ts - Functions for gathering context and sending bug reports to server
 */

import type { BugReportRequest, BugReportResponse } from "@game/domain";

let getGameState: (() => any) | null = null;
let getPlayerState: (() => any) | null = null;

/**
 * Set game state dependencies from main client
 * @param deps - Functions to get current game and player state
 */
export function setBugReportDependencies(deps: {
  getGameState: () => any;
  getPlayerState: () => any;
}): void {
  getGameState = deps.getGameState;
  getPlayerState = deps.getPlayerState;
}

/**
 * Collect current game state for bug report context
 * @returns Game state object or null if not available
 */
function collectGameState(): BugReportRequest["gameState"] | null {
  try {
    if (!getGameState || !getPlayerState) {
      return null;
    }

    const gameState = getGameState();
    const playerState = getPlayerState();

    if (!gameState || !playerState) {
      return null;
    }

    return {
      playerId: playerState.id || "unknown",
      playerName: playerState.name || "unknown",
      lobbyId: gameState.lobbyId || undefined,
      playerCount: gameState.players?.length || undefined,
      gameStarted: gameState.started || false,
      position:
        playerState.pos?.x !== undefined && playerState.pos?.y !== undefined
          ? { x: playerState.pos.x, y: playerState.pos.y }
          : undefined,
      health: playerState.health || undefined,
      kills: playerState.kills || undefined,
      deaths: playerState.deaths || undefined,
    };
  } catch (error) {
    console.warn("Failed to collect game state for bug report:", error);
    return null;
  }
}

/**
 * Collect browser information for bug report context
 * @returns Browser info object
 */
function collectBrowserInfo(): BugReportRequest["browserInfo"] {
  return {
    userAgent: navigator.userAgent,
    url: window.location.href,
    timestamp: Date.now(),
  };
}

/**
 * Collect recent console errors for bug report context
 * @returns Array of recent error messages
 */
function collectConsoleErrors(): string[] {
  // For now, return empty array - in a real implementation,
  // you would capture console.error calls in a buffer
  // This could be enhanced by overriding console.error and storing recent errors
  const errors: string[] = [];

  // Check for any stored errors in a global error handler
  if ((window as any).__bugReportErrors) {
    errors.push(...(window as any).__bugReportErrors);
  }

  return errors;
}

/**
 * Submit bug report to server
 * @param message - User's bug description
 * @returns Promise with server response
 */
export async function submitBugReport(
  message: string,
): Promise<BugReportResponse> {
  try {
    if (!message || message.trim().length === 0) {
      return {
        success: false,
        error: "Bug report message cannot be empty",
      };
    }

    const bugReport: BugReportRequest = {
      message: message.trim(),
      gameState: collectGameState() ?? undefined,
      browserInfo: collectBrowserInfo(),
      consoleErrors: collectConsoleErrors(),
    };

    const response = await fetch("/api/bug-report", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(bugReport),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Bug report HTTP error ${response.status}: ${errorText}`);
      return {
        success: false,
        error: `Server error: ${response.status}`,
      };
    }

    const result = (await response.json()) as BugReportResponse;

    if (result.success) {
      console.log(`Bug report submitted successfully: ${result.issueUrl}`);
    } else {
      console.error(`Bug report rejected by server: ${result.error}`);
    }

    return result;
  } catch (error) {
    console.error("Failed to submit bug report:", error);
    return {
      success: false,
      error: "Failed to submit bug report",
    };
  }
}

/**
 * Setup global error capture for bug reports
 * Call this early in application initialization
 */
export function setupErrorCapture(): void {
  const maxErrors = 10;
  const errors: string[] = [];

  (window as any).__bugReportErrors = errors;

  const originalError = console.error;
  console.error = (...args) => {
    originalError.apply(console, args);

    const errorMessage = args
      .map((arg) =>
        typeof arg === "object" ? JSON.stringify(arg, null, 2) : String(arg),
      )
      .join(" ");

    errors.push(`[${new Date().toISOString()}] ${errorMessage}`);

    if (errors.length > maxErrors) {
      errors.shift();
    }
  };

  window.addEventListener("unhandledrejection", (event) => {
    const errorMessage = `Unhandled Promise Rejection: ${event.reason}`;
    errors.push(`[${new Date().toISOString()}] ${errorMessage}`);

    if (errors.length > maxErrors) {
      errors.shift();
    }
  });

  window.addEventListener("error", (event) => {
    const errorMessage = `Global Error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`;
    errors.push(`[${new Date().toISOString()}] ${errorMessage}`);

    if (errors.length > maxErrors) {
      errors.shift();
    }
  });
}

/**
 * Get bug reporting system statistics for debugging
 * @returns Object with current bug reporting state
 */
export function getBugReportStats(): {
  dependenciesSet: boolean;
  capturedErrors: number;
  hasGameState: boolean;
  hasPlayerState: boolean;
} {
  return {
    dependenciesSet: !!(getGameState && getPlayerState),
    capturedErrors:
      ((window as any).__bugReportErrors as string[])?.length || 0,
    hasGameState: !!getGameState?.(),
    hasPlayerState: !!getPlayerState?.(),
  };
}
